import React, { useState, useEffect, useRef } from 'react';
import { fetchData } from '../api';

interface ControlDebugEntry {
  timestamp: number;
  type: 'decision' | 'command' | 'info' | 'status';
  action: 'derate' | 'increase' | 'hold' | 'send' | 'skip' | 'monitor';
  message: string;
  data?: {
    currentSolarPower?: number;
    targetSolarPower?: number;
    systemCapacity?: number;
    previousLimit?: number;
    newLimit?: number;
    currentLimit?: number;
    deratePower?: number;
    increasePower?: number;
    batteryHeadroom?: number;
    batteryPower?: number;
    meterPower?: number;
    meterPhase1?: number;
    meterPhase2?: number;
    meterPhase3?: number;
    exportHardLimit?: number;
    exportSoftLimit?: number;
    meterTarget?: number;
    errorFromHardLimit?: number;
    errorFromSoftLimit?: number;
    portId?: string;
  };
}

interface CommandHistoryEntry {
  timestamp: number;
  portId: string;
  rateLimit: number;
}

interface ControlSnapshot {
  currentSolarPower: number;
  systemCapacity: number;
  currentLimit: number;
  previousLimit: number;
  deratePower: number;
  increasePower: number;
  batteryHeadroom: number;
  batteryPower: number;
  meterPower: number;
  meterPhase1: number;
  meterPhase2: number;
  meterPhase3: number;
  exportHardLimit: number;
  exportSoftLimit: number;
  meterTarget: number;
  errorFromHardLimit: number;
  errorFromSoftLimit: number;
  potentialSolarPower: number;
  curtailedPower: number;
  deratingEmergency: boolean;
  batteryAbsorptionExpected: number;
}

interface ControlSettings {
  exportPowerHardLimit: number;
  exportPowerSoftLimit: number;
  meterTarget: number;
  perPhaseLimit: boolean;
  deratingKp: number;
  deratingKi: number;
  deratingLimit: number;
  increasingLimit: number;
  controlCommandInterval: number;
  postControlDelay: number;
  periodicControlEnabled: boolean;
  periodicControlInterval: number;
}

interface ControlDebugData {
  log: ControlDebugEntry[];
  commandHistory: CommandHistoryEntry[];
  snapshot: ControlSnapshot;
  settings: ControlSettings;
  currentState: {
    currentRateLimit: number;
    lastSentRateLimit: number;
    lastControlCommandTime: number;
    deratePower: number;
    increasePower: number;
    systemCapacity: number;
    solarPower: number;
    potentialSolarPower: number;
    curtailedPower: number;
    meterPower: { total: number; phase1: number; phase2: number; phase3: number };
    availableBatteryChargeHeadroom: number;
  };
}

export function ControlDebugPanel() {
  const [data, setData] = useState<ControlDebugData | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showStatusLogs, setShowStatusLogs] = useState(false);
  const logContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchControlDebug = async () => {
      try {
        const result = await fetchData('/controlDebug');
        setData(result);
      } catch (err) {
        console.error('Failed to fetch control debug data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchControlDebug();
    const interval = setInterval(fetchControlDebug, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [data, autoScroll]);

  const formatTime = (ts: number) => {
    return new Date(ts).toLocaleTimeString('en-GB', { hour12: false });
  };

  const formatTimeSince = (ts: number) => {
    const seconds = Math.floor((Date.now() - ts) / 1000);
    if (seconds < 1) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
  };

  const formatPower = (watts: number, showSign = false) => {
    if (Math.abs(watts) >= 1000) {
      const val = (watts / 1000).toFixed(1);
      return showSign && watts > 0 ? `+${val} kW` : `${val} kW`;
    }
    return showSign && watts > 0 ? `+${watts} W` : `${watts} W`;
  };

  const actionColors: Record<string, string> = {
    derate: 'text-red-400',
    increase: 'text-green-400',
    hold: 'text-slate-400',
    send: 'text-cyan-400',
    skip: 'text-slate-500',
    monitor: 'text-blue-400'
  };

  const actionIcons: Record<string, string> = {
    derate: '▼',
    increase: '▲',
    hold: '●',
    send: '→',
    skip: '○',
    monitor: '◈'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const snapshot = data?.snapshot;
  const settings = data?.settings;
  const state = data?.currentState;
  const limitPercent = snapshot ? (snapshot.currentLimit / 100).toFixed(1) : '100.0';

  // Filter logs based on showStatusLogs toggle
  const filteredLogs = showStatusLogs
    ? data?.log
    : data?.log?.filter(e => e.type !== 'status');

  return (
    <div className="space-y-4">
      {/* Current State Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
          <div className="text-xs text-slate-500 uppercase">Rate Limit</div>
          <div className={`text-2xl font-bold font-mono ${
            snapshot && snapshot.currentLimit < 10000 ? 'text-orange-400' : 'text-green-400'
          }`}>
            {limitPercent}%
          </div>
        </div>
        <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
          <div className="text-xs text-slate-500 uppercase">Solar Power</div>
          <div className="text-2xl font-bold font-mono text-amber-400">
            {snapshot ? formatPower(snapshot.currentSolarPower) : '0 W'}
          </div>
        </div>
        <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
          <div className="text-xs text-slate-500 uppercase">Grid Export</div>
          <div className={`text-2xl font-bold font-mono ${
            snapshot && snapshot.meterPower >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {snapshot ? formatPower(snapshot.meterPower) : '0 W'}
          </div>
        </div>
        <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
          <div className="text-xs text-slate-500 uppercase">Curtailed</div>
          <div className={`text-2xl font-bold font-mono ${
            snapshot && snapshot.curtailedPower > 0 ? 'text-purple-400' : 'text-slate-500'
          }`}>
            {snapshot ? formatPower(snapshot.curtailedPower) : '0 W'}
          </div>
        </div>
        <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
          <div className="text-xs text-slate-500 uppercase">Battery</div>
          <div className={`text-2xl font-bold font-mono ${
            snapshot && snapshot.batteryPower < -100 ? 'text-blue-400' :
            snapshot && snapshot.batteryPower > 100 ? 'text-orange-400' : 'text-slate-500'
          }`}>
            {snapshot ? formatPower(snapshot.batteryPower) : '0 W'}
          </div>
        </div>
      </div>

      {/* Control Math - The Important Part */}
      <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
        <h3 className="text-sm font-semibold text-slate-300 uppercase mb-3">Control Math</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left: Meter & Limits */}
          <div className="space-y-2 font-mono text-sm">
            <div className="text-xs text-slate-500 uppercase mb-2">Meter vs Limits</div>
            <div className="flex justify-between">
              <span className="text-slate-400">Meter Total:</span>
              <span className={snapshot && snapshot.meterPower > (settings?.exportPowerHardLimit || 0) ? 'text-red-400 font-bold' : 'text-white'}>
                {snapshot ? formatPower(snapshot.meterPower) : '?'}
              </span>
            </div>
            <div className="flex justify-between text-slate-500 text-xs pl-4">
              <span>L1: {snapshot ? formatPower(snapshot.meterPhase1) : '?'}</span>
              <span>L2: {snapshot ? formatPower(snapshot.meterPhase2) : '?'}</span>
              <span>L3: {snapshot ? formatPower(snapshot.meterPhase3) : '?'}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-slate-700">
              <span className="text-slate-400">Hard Limit:</span>
              <span className="text-red-300">{settings ? formatPower(settings.exportPowerHardLimit) : '?'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Soft Limit:</span>
              <span className="text-green-300">{settings ? formatPower(settings.exportPowerSoftLimit) : '?'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Target:</span>
              <span className="text-blue-300">{settings ? formatPower(settings.meterTarget) : '?'}</span>
            </div>
          </div>

          {/* Right: Error & Control */}
          <div className="space-y-2 font-mono text-sm">
            <div className="text-xs text-slate-500 uppercase mb-2">Error Calculation</div>
            <div className="flex justify-between">
              <span className="text-slate-400">Error (vs Hard):</span>
              <span className={snapshot && snapshot.errorFromHardLimit > 0 ? 'text-red-400 font-bold' : 'text-green-400'}>
                {snapshot ? formatPower(snapshot.errorFromHardLimit, true) : '?'}
              </span>
            </div>
            <div className="text-xs text-slate-500 pl-4">
              {snapshot && snapshot.errorFromHardLimit > 0
                ? '↑ Over hard limit - must derate!'
                : '↓ Under hard limit - OK'}
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Error (vs Soft):</span>
              <span className={snapshot && snapshot.errorFromSoftLimit > 0 ? 'text-green-400' : 'text-slate-400'}>
                {snapshot ? formatPower(snapshot.errorFromSoftLimit, true) : '?'}
              </span>
            </div>
            <div className="text-xs text-slate-500 pl-4">
              {snapshot && snapshot.errorFromSoftLimit > 0
                ? '↑ Under soft limit - can increase'
                : '↓ Above soft limit'}
            </div>
            <div className="flex justify-between pt-2 border-t border-slate-700">
              <span className="text-slate-400">→ Derate Req:</span>
              <span className={snapshot && snapshot.deratePower > 0 ? 'text-red-400 font-bold' : 'text-slate-500'}>
                {snapshot ? formatPower(snapshot.deratePower) : '0 W'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">→ Increase Req:</span>
              <span className={snapshot && snapshot.increasePower > 0 ? 'text-green-400 font-bold' : 'text-slate-500'}>
                {snapshot ? formatPower(snapshot.increasePower) : '0 W'}
              </span>
            </div>
          </div>
        </div>

        {/* Battery Info */}
        {snapshot && (
          <div className="mt-3 pt-3 border-t border-slate-700 font-mono text-sm">
            <div className="text-xs text-slate-500 uppercase mb-2">Battery</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <div className="flex justify-between">
                <span className="text-slate-400">Power:</span>
                <span className={
                  snapshot.batteryPower < -100 ? 'text-blue-400' :
                  snapshot.batteryPower > 100 ? 'text-orange-400' : 'text-slate-500'
                }>
                  {formatPower(snapshot.batteryPower)}
                  {snapshot.batteryPower < -100 ? ' (charging)' :
                   snapshot.batteryPower > 100 ? ' (discharging)' : ''}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Headroom:</span>
                <span className={snapshot.batteryHeadroom > 0 ? 'text-blue-300' : 'text-slate-500'}>
                  {formatPower(snapshot.batteryHeadroom)}
                </span>
              </div>
            </div>
            {snapshot.batteryAbsorptionExpected > 0 && (
              <div className="text-xs text-blue-400 mt-2">
                ↳ Expecting battery to absorb {formatPower(snapshot.batteryAbsorptionExpected)}
              </div>
            )}
          </div>
        )}

        {/* Emergency Warning */}
        {snapshot?.deratingEmergency && (
          <div className="mt-3 p-2 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-sm">
            ⚠ EMERGENCY DERATE ACTIVE - Immediate curtailment required!
          </div>
        )}
      </div>

      {/* PI Controller Settings (collapsible) */}
      <details className="bg-slate-800 rounded-lg border border-slate-700">
        <summary className="p-3 cursor-pointer text-sm font-semibold text-slate-400 uppercase hover:text-slate-300">
          PI Controller Settings
        </summary>
        <div className="p-3 pt-0 grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-sm">
          <div>
            <div className="text-xs text-slate-500">Kp</div>
            <div className="text-white">{settings?.deratingKp || '?'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Ki</div>
            <div className="text-white">{settings?.deratingKi || '?'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Derate Limit</div>
            <div className="text-white">{settings ? formatPower(settings.deratingLimit) : '?'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Increase Limit</div>
            <div className="text-white">{settings ? formatPower(settings.increasingLimit) : '?'}</div>
          </div>
        </div>
      </details>

      {/* Command History */}
      <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-300 uppercase">Recent Commands</h3>
          <span className="text-xs text-slate-500">
            Last: {state?.lastControlCommandTime ? formatTimeSince(state.lastControlCommandTime) : 'never'}
          </span>
        </div>
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {data?.commandHistory?.slice(-10).reverse().map((cmd, idx) => (
            <div key={idx} className="flex items-center gap-3 text-xs font-mono">
              <span className="text-slate-500">{formatTime(cmd.timestamp)}</span>
              <span className="text-cyan-400">→</span>
              <span className="text-slate-400">{cmd.portId}</span>
              <span className={`px-1.5 py-0.5 rounded ${
                cmd.rateLimit < 10000 ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'
              }`}>
                {(cmd.rateLimit / 100).toFixed(1)}%
              </span>
            </div>
          )) || (
            <div className="text-slate-500 text-xs">No commands sent yet</div>
          )}
        </div>
      </div>

      {/* Decision Log */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="flex items-center justify-between p-3 border-b border-slate-700">
          <h3 className="text-sm font-semibold text-slate-300 uppercase">Control Log</h3>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input
                type="checkbox"
                checked={showStatusLogs}
                onChange={(e) => setShowStatusLogs(e.target.checked)}
                className="rounded bg-slate-700 border-slate-600"
              />
              Show status
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input
                type="checkbox"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
                className="rounded bg-slate-700 border-slate-600"
              />
              Auto-scroll
            </label>
          </div>
        </div>
        <div
          ref={logContainerRef}
          className="p-2 space-y-1 max-h-64 overflow-y-auto bg-slate-950"
        >
          {filteredLogs?.slice(-50).map((entry, idx) => (
            <div key={idx} className={`font-mono text-xs py-1 border-b border-slate-800 last:border-0 ${
              entry.type === 'status' ? 'opacity-60' : ''
            }`}>
              <div className="flex items-start gap-2">
                <span className="text-slate-500">[{formatTime(entry.timestamp)}]</span>
                <span className={`${actionColors[entry.action] || 'text-slate-400'}`}>
                  {actionIcons[entry.action] || '●'}
                </span>
                <span className="text-slate-300 flex-1">{entry.message}</span>
              </div>
              {entry.data && entry.type !== 'status' && (
                <div className="ml-20 mt-1 text-slate-500 flex flex-wrap gap-3">
                  {entry.data.meterPower !== undefined && (
                    <span>meter: {entry.data.meterPower}W</span>
                  )}
                  {entry.data.currentSolarPower !== undefined && (
                    <span>solar: {entry.data.currentSolarPower}W</span>
                  )}
                  {entry.data.targetSolarPower !== undefined && (
                    <span>→target: {entry.data.targetSolarPower}W</span>
                  )}
                  {entry.data.previousLimit !== undefined && entry.data.newLimit !== undefined && (
                    <span className="text-cyan-400">
                      {(entry.data.previousLimit / 100).toFixed(1)}% → {(entry.data.newLimit / 100).toFixed(1)}%
                    </span>
                  )}
                  {entry.data.batteryHeadroom !== undefined && entry.data.batteryHeadroom > 0 && (
                    <span className="text-blue-400">batt: {entry.data.batteryHeadroom}W</span>
                  )}
                </div>
              )}
            </div>
          )) || (
            <div className="text-slate-500 text-sm p-4 text-center">
              No control activity yet. Waiting for data...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
